import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import PropTypes from 'prop-types';
import FlexBox from 'components/common/FlexBox';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { ALLOWED_PARTNER_TYPES, PARTNER_CONSENTS, PARTNER_CONSENT_TEMPLATES } from 'features/partnerConsents/queries';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import useQuery from 'hooks/apollo/useQuery';
import React, { useEffect, useMemo, useState } from 'react';
import PartnerConsentComponent from './PartnerConsentComponent';
import { REQUIRED_DOC_TYPES_QUERY } from 'features/documentTemplates/edit/queries';
import { withAreaGetter } from 'providers/AreasConsumers';
import { areas } from 'constants/areas';
import Portal from 'utils/portal/Portal';
import IconButton from 'components/common/buttons/IconButton';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSubscription from 'hooks/apollo/useSubscription';
import { useMutation } from 'hooks/apollo/useMutation';
import { useToast } from 'hooks/toasts/useToast';
import { SAVE_PARTNER_CONSENTS } from '../mutations';
import { PARTNER_CONSENTS_SAVED } from '../subscriptions';
import { Card, Typography } from '@totalsoft/rocket-ui';
import EditIcon from '@mui/icons-material/Edit';
import { items, required, shape } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_CONSENTS_ON_PARTNER_TYPES } from 'constants/rightsCodes';

const validator = items(
  shape({
    code: required,
    name: required
  })
);

const PartnerConsentsContainer = ({ getElemByAreaKey }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const hasPermission = usePermission(ADMIN_CONSENTS_ON_PARTNER_TYPES);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate] = useDirtyFieldValidation(validator);

  const [pending, setPending] = useState(false);

  // #region Load data

  const { loading: consentsLoading } = useQuery(PARTNER_CONSENTS, {
    onCompleted: data => {
      resetLens(data.partnerConsents || []);
    }
  });

  const { data: templatesData, loading: loadingTemplatesData } = useQuery(PARTNER_CONSENT_TEMPLATES);

  const { data: requiredDocTypesData, loading: loadingRequiredDocTypes } = useQuery(REQUIRED_DOC_TYPES_QUERY);

  const { data: partnerTypesData, loading: loadingPartnerTypes } = useQuery(ALLOWED_PARTNER_TYPES);

  const templates = useMemo(() => templatesData?.partnerConsentTemplates || [], [templatesData]);

  const requiredDocTypes = useMemo(() => requiredDocTypesData?.requiredDocTypes || [], [requiredDocTypesData]);

  const partnerTypes = useMemo(() => partnerTypesData?.allowedPartnerTypes || [], [partnerTypesData]);

  // #endregion

  const handleAdd = () => {
    const currentConsents = lens |> get;
    (lens |> set)([...currentConsents, { id: crypto.randomUUID(), partnerTypeIds: [] }]);
    setTimeout(() => {
      document.getElementById('partner-consents-list-bottom').scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  const handleRemove = id => {
    const currentConsents = lens |> get;
    (lens |> set)(currentConsents.filter(consent => consent.id !== id));
  };

  // #region Save

  const [savePartnerConsents] = useMutation(SAVE_PARTNER_CONSENTS, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const partnerConsents = data.map(
      ({ id, code, name, description, templateId, requiredDocTypeId, validityDays, isActive, partnerTypeIds }) => ({
        id: typeof id === 'string' ? null : id,
        code,
        name,
        description,
        templateId,
        requiredDocTypeId,
        validityDays,
        isActive,
        partnerTypeIds
      })
    );

    setPending(true);

    savePartnerConsents({ variables: { input: { partnerConsents } } });
  };

  useSubscription(PARTNER_CONSENTS_SAVED, {
    onError: error => addToast(error, 'error'),
    onData: ({ client, data }) => {
      const newPartnerConsents = data.data.partnerConsentsSaved.partnerConsents;
      resetLens(newPartnerConsents);
      setPending(false);
      addToast(t('PartnerConsent.Saved'), 'success');
      client.writeQuery({
        query: PARTNER_CONSENTS,
        data: { partnerConsents: newPartnerConsents }
      });
    }
  });

  // #endregion

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const loading = useMemo(
    () => consentsLoading || loadingTemplatesData || loadingRequiredDocTypes || loadingPartnerTypes,
    [consentsLoading, loadingRequiredDocTypes, loadingTemplatesData, loadingPartnerTypes]
  );

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const disabled = useMemo(() => !hasPermission || pending, [hasPermission, pending]);

  const hasConsents = (lens.length |> get) > 0;

  return (
    <Card icon={EditIcon}>
      <FlexBox direction='column' gap={2}>
        <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
        {loading && <WrappedFakeText />}
        {!loading && !hasConsents && (
          <Typography fontWeight='bold' align='center'>
            {t('PartnerConsent.NoConsents')}
          </Typography>
        )}
        {!loading &&
          hasConsents &&
          (lens |> sequence).map((consentLens, index) => (
            <PartnerConsentComponent
              key={consentLens.id |> get}
              lens={consentLens}
              templates={templates}
              requiredDocTypes={requiredDocTypes}
              partnerTypes={partnerTypes}
              onRemove={handleRemove}
              validation={validation[index]}
              disabled={disabled}
            />
          ))}
        <div id='partner-consents-list-bottom'></div>
        <Portal container={getElemByAreaKey(areas.headerLinks)}>
          <IconButton type='add' onClick={handleAdd} forHeader disabled={pending} />
          <IconButton type='save' onClick={handleSave} forHeader disabled={!isDirty || pending} />
        </Portal>
      </FlexBox>
    </Card>
  );
};

PartnerConsentsContainer.propTypes = {
  getElemByAreaKey: PropTypes.func
};

export default withAreaGetter(PartnerConsentsContainer);
